import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Lock from 'lucide-react/dist/esm/icons/lock';
import Plus from 'lucide-react/dist/esm/icons/plus';
import Copy from 'lucide-react/dist/esm/icons/copy';
import Eye from 'lucide-react/dist/esm/icons/eye';
import EyeOff from 'lucide-react/dist/esm/icons/eye-off';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle';
import ExternalLink from 'lucide-react/dist/esm/icons/external-link';
import Upload from 'lucide-react/dist/esm/icons/upload';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import Globe from 'lucide-react/dist/esm/icons/globe';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { encryptData, decryptData } from '../../lib/encryption';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { SearchInput } from '../common/SearchInput';

interface DigitalAsset {
  id: string;
  name: string;
  type: string;
  url?: string;
  username?: string;
  encrypted_password?: string;
  notes?: string;
  strength?: 'weak' | 'medium' | 'strong';
  favorite?: boolean;
  last_used?: string;
  shared_with?: string[];
  category?: string;
}

interface Category {
  id: string;
  name: string;
  color: string;
}

const defaultCategories: Category[] = [
  { id: 'social', name: 'Social Media', color: 'bg-blue-100 text-blue-800' },
  { id: 'financial', name: 'Financial', color: 'bg-green-100 text-green-800' },
  { id: 'email', name: 'Email', color: 'bg-purple-100 text-purple-800' },
  { id: 'shopping', name: 'Shopping', color: 'bg-pink-100 text-pink-800' },
  { id: 'work', name: 'Work', color: 'bg-orange-100 text-orange-800' },
  { id: 'other', name: 'Other', color: 'bg-gray-100 text-gray-800' },
  { id: 'website', name: 'Website', color: 'bg-indigo-100 text-indigo-800' },
];

export function PasswordVault() {
  const { user } = useAuth();
  const [passwords, setPasswords] = useState<DigitalAsset[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [expandedPasswords, setExpandedPasswords] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [decryptedPasswords, setDecryptedPasswords] = useState<Record<string, string>>({});
  const [newPassword, setNewPassword] = useState({
    title: '',
    username: '',
    password: '',
    url: '',
    category: 'other',
    notes: '',
  });

  // Import functionality state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importType, setImportType] = useState<'chrome' | 'csv' | null>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState<{
    total: number;
    imported: number;
    failed: number;
    errors: string[];
  } | null>(null);

  useEffect(() => {
    if (user) {
      loadPasswords();
    }
  }, [user]);

  useEffect(() => {
    const handler = () => { if (user) loadPasswords(true); };
    window.addEventListener('refresh-digital', handler);
    return () => window.removeEventListener('refresh-digital', handler);
  }, [user]);

  async function loadPasswords(background = false) {
    try {
      if (!background) setLoading(true);
      const { data, error } = await supabase
        .from('digital_assets')
        .select('*')
        .eq('user_id', user?.id)
        .eq('type', 'password');

      if (error) throw error;
      setPasswords(data || []);
    } catch (err) {
      if (!background) setError(err instanceof Error ? err.message : 'Failed to load passwords');
    } finally {
      if (!background) setLoading(false);
    }
  }

  const decryptPassword = async (encryptedPassword: string, passwordId: string) => {
    try {
      const decrypted = await decryptData(encryptedPassword);
      setDecryptedPasswords(prev => ({
        ...prev,
        [passwordId]: decrypted
      }));
      return decrypted;
    } catch (err) {
      console.error('Failed to decrypt password:', err);
      setError('Failed to decrypt password');
      return '';
    }
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  const togglePasswordVisibility = async (id: string, encryptedPassword?: string) => {
    if (!encryptedPassword) return;

    // If we're showing the password and haven't decrypted it yet, decrypt it
    if (!showPasswords[id] && !decryptedPasswords[id]) {
      await decryptPassword(encryptedPassword, id);
    }

    setShowPasswords(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleExpandedPassword = (id: string) => {
    setExpandedPasswords(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleFavorite = async (id: string, currentFavorite: boolean = false) => {
    try {
      const { error } = await supabase
        .from('digital_assets')
        .update({ favorite: !currentFavorite })
        .eq('id', id);

      if (error) throw error;

      setPasswords(prev => 
        prev.map(password => 
          password.id === id 
            ? { ...password, favorite: !currentFavorite } 
            : password
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update favorite status');
    }
  };

  const deletePassword = async (id: string) => {
    try {
      const { error } = await supabase
        .from('digital_assets')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setPasswords(prev => prev.filter(password => password.id !== id));
      setExpandedPasswords(prev => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
      setShowPasswords(prev => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete password');
    }
  };

  const updateLastUsed = async (id: string) => {
    try {
      const { error } = await supabase
        .from('digital_assets')
        .update({ last_used: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    } catch (err) {
      console.error('Failed to update last used timestamp:', err);
    }
  };

  // Import functions
  const importFromChrome = async () => {
    try {
      setImporting(true);
      setImportProgress(0);
      setImportResults(null);

      // Check if Chrome extension is available
      if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
        throw new Error('Chrome extension not available. Please install the iDecide Chrome extension.');
      }

      // Request passwords from Chrome extension
      const response = await chrome.runtime.sendMessage({ action: 'getPasswords' });
      
      if (!response || !response.passwords) {
        throw new Error('No passwords found or extension not responding');
      }

      const passwords = response.passwords;
      setImportProgress(10);

      let imported = 0;
      let failed = 0;
      const errors: string[] = [];

      for (let i = 0; i < passwords.length; i++) {
        try {
          const password = passwords[i];
          
          // Skip if no name or password
          if (!password.name || !password.password) {
            failed++;
            errors.push(`Skipped ${password.name || 'unnamed'}: Missing required fields`);
            continue;
          }

          // Check if password already exists
          const existing = passwords.find(p => 
            p.name === password.name && p.url === password.url
          );
          
          if (existing && existing !== password) {
            failed++;
            errors.push(`Skipped ${password.name}: Duplicate entry`);
            continue;
          }

          // Encrypt password
          const encryptedPassword = await encryptData(password.password);

          // Save to database
          const { error: insertError } = await supabase
            .from('digital_assets')
            .insert({
              user_id: user?.id,
              name: password.name,
              type: 'password',
              url: password.url || '',
              username: password.username || '',
              encrypted_password: encryptedPassword,
              notes: `Imported from Chrome on ${new Date().toLocaleDateString()}`,
              category: 'other',
              strength: 'medium',
              favorite: false,
              last_used: new Date().toISOString()
            });

          if (insertError) {
            failed++;
            errors.push(`Failed to import ${password.name}: ${insertError.message}`);
          } else {
            imported++;
          }

          // Update progress
          setImportProgress(Math.round(((i + 1) / passwords.length) * 80) + 10);

        } catch (err) {
          failed++;
          errors.push(`Error importing password: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
      }

      setImportProgress(100);
      setImportResults({
        total: passwords.length,
        imported,
        failed,
        errors: errors.slice(0, 10) // Show only first 10 errors
      });

      // Reload passwords
      await loadPasswords();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import from Chrome');
      setImportResults({
        total: 0,
        imported: 0,
        failed: 1,
        errors: [err instanceof Error ? err.message : 'Unknown error']
      });
    } finally {
      setImporting(false);
    }
  };

  const importFromCSV = async (file: File) => {
    try {
      setImporting(true);
      setImportProgress(0);
      setImportResults(null);

      const text = await file.text();
      const lines = text.split('\n');
      
      // Parse CSV (simple implementation - assumes comma-separated with headers)
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const data = lines.slice(1).filter(line => line.trim());

      setImportProgress(10);

      let imported = 0;
      let failed = 0;
      const errors: string[] = [];

      // Find required columns
      const nameIndex = headers.findIndex(h => h.includes('name') || h.includes('title') || h.includes('site'));
      const usernameIndex = headers.findIndex(h => h.includes('username') || h.includes('user') || h.includes('email'));
      const passwordIndex = headers.findIndex(h => h.includes('password') || h.includes('pass'));
      const urlIndex = headers.findIndex(h => h.includes('url') || h.includes('website') || h.includes('site'));

      if (passwordIndex === -1) {
        throw new Error('No password column found in CSV. Please ensure your CSV has a password column.');
      }

      for (let i = 0; i < data.length; i++) {
        try {
          const values = data[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          
          const name = nameIndex !== -1 ? values[nameIndex] : `Imported Password ${i + 1}`;
          const username = usernameIndex !== -1 ? values[usernameIndex] : '';
          const password = values[passwordIndex];
          const url = urlIndex !== -1 ? values[urlIndex] : '';

          // Skip if no password
          if (!password) {
            failed++;
            errors.push(`Skipped row ${i + 2}: No password`);
            continue;
          }

          // Encrypt password
          const encryptedPassword = await encryptData(password);

          // Save to database
          const { error: insertError } = await supabase
            .from('digital_assets')
            .insert({
              user_id: user?.id,
              name,
              type: 'password',
              url,
              username,
              encrypted_password: encryptedPassword,
              notes: `Imported from CSV on ${new Date().toLocaleDateString()}`,
              category: 'other',
              strength: 'medium',
              favorite: false,
              last_used: new Date().toISOString()
            });

          if (insertError) {
            failed++;
            errors.push(`Failed to import ${name}: ${insertError.message}`);
          } else {
            imported++;
          }

          // Update progress
          setImportProgress(Math.round(((i + 1) / data.length) * 80) + 10);

        } catch (err) {
          failed++;
          errors.push(`Error importing row ${i + 2}: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
      }

      setImportProgress(100);
      setImportResults({
        total: data.length,
        imported,
        failed,
        errors: errors.slice(0, 10) // Show only first 10 errors
      });

      // Reload passwords
      await loadPasswords();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import from CSV');
      setImportResults({
        total: 0,
        imported: 0,
        failed: 1,
        errors: [err instanceof Error ? err.message : 'Unknown error']
      });
    } finally {
      setImporting(false);
    }
  };

  const handleImportClick = (type: 'chrome' | 'csv') => {
    setImportType(type);
    setShowImportModal(true);
    setImportResults(null);
    setImportProgress(0);
  };

  const handleCSVFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      importFromCSV(file);
    }
  };

  const handleSavePassword = async () => {
    try {
      if (!user) {
        setError('User not authenticated');
        return;
      }

      if (!newPassword.title || !newPassword.password) {
        setError('Title and password are required');
        return;
      }

      // Encrypt the password before saving
      const encryptedPassword = await encryptData(newPassword.password);

      const { data, error } = await supabase
        .from('digital_assets')
        .insert({
          user_id: user.id,
          name: newPassword.title,
          type: 'password',
          url: newPassword.url,
          username: newPassword.username,
          encrypted_password: encryptedPassword,
          notes: newPassword.notes,
          category: newPassword.category,
          strength: 'medium', // You could add password strength calculation here
          favorite: false,
          last_used: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      // Add the new password to the list
      setPasswords(prev => [...prev, data]);

      // Reset the form
      setNewPassword({
        title: '',
        username: '',
        password: '',
        url: '',
        category: 'other',
        notes: '',
      });

      // Close the modal
      setShowAddModal(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save password');
    }
  };

  const filteredPasswords = passwords.filter(password => {
    const matchesSearch =
      password.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (password.username?.toLowerCase().includes(searchQuery.toLowerCase()) || false);

    // Handle three states for `selectedCategory`
    let matchesCategory = true;
    if (selectedCategory === 'favorites') {
      matchesCategory = !!password.favorite;
    } else if (selectedCategory) {
      matchesCategory = password.category === selectedCategory;
    }

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Password Vault</h1>
          <p className="text-gray-600 mt-1">Securely store and manage your passwords</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => handleImportClick('chrome')}
            icon={<Globe size={20} />}
          >
            Import from Chrome
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleImportClick('csv')}
            icon={<FileText size={20} />}
          >
            Import from CSV
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowAddModal(true)}
            icon={<Plus size={20} />}
          >
            Add Password
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
          <AlertCircle size={20} />
          <p>{error}</p>
        </div>
      )}

      <div className="flex gap-4">
        <div className="w-64 space-y-6">
          <Card>
            <div className="p-4">
              <h2 className="font-semibold text-gray-900 mb-4">Categories</h2>
              <div className="space-y-2">
                {/* All */}
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors
                    ${!selectedCategory ? 'bg-idecide-primary text-idecide-accent' : 'hover:bg-gray-100'}`}
                >
                  All Passwords
                </button>

                {/* Favorites */}
                <button
                  onClick={() => setSelectedCategory('favorites')}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors
                    ${selectedCategory === 'favorites' ? 'bg-idecide-primary text-idecide-accent' : 'hover:bg-gray-100'}`}
                >
                  Favourites
                </button>

                {defaultCategories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors
                      ${selectedCategory === category.id ? 'bg-idecide-primary text-idecide-accent' : 'hover:bg-gray-100'}`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          </Card>
        </div>

        <div className="flex-1 space-y-6">
          <div className="flex gap-4">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search passwords..."
              className="flex-1"
            />
          </div>

          <Card>
            <div className="p-6">
              <div className="space-y-4">
                {filteredPasswords.length > 0 ? (
                  filteredPasswords.map(password => (
                    <motion.div
                      key={password.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      {/* Main Password Row */}
                      <div className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-white rounded-lg">
                            <Lock className="text-gray-600" size={20} />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{password.name}</h3>
                            <p className="text-sm text-gray-500">{password.username}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Favorite Button */}
                          <button
                            onClick={() => toggleFavorite(password.id, password.favorite)}
                            className={`p-2 ${password.favorite ? 'text-yellow-500' : 'text-gray-500 hover:text-gray-700'}`}
                            title={password.favorite ? 'Remove from favorites' : 'Add to favorites'}
                          >
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              width="16" 
                              height="16" 
                              viewBox="0 0 24 24" 
                              fill={password.favorite ? "currentColor" : "none"} 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            >
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                            </svg>
                          </button>
                          
                          {/* Show/Hide Credentials Button */}
                          <button
                            onClick={() => togglePasswordVisibility(password.id, password.encrypted_password)}
                            className="p-2 text-gray-500 hover:text-gray-700"
                            title={showPasswords[password.id] ? 'Hide credentials' : 'Show credentials'}
                            disabled={!password.encrypted_password}
                          >
                            {showPasswords[password.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          
                          {/* Expand/Collapse Button */}
                          <button
                            onClick={() => toggleExpandedPassword(password.id)}
                            className="p-2 text-gray-500 hover:text-gray-700"
                            title={expandedPasswords[password.id] ? 'Hide details' : 'Show details'}
                          >
                            {expandedPasswords[password.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                          
                          {/* Delete Button */}
                          <button
                            onClick={() => deletePassword(password.id)}
                            className="p-2 text-red-500 hover:text-red-700"
                            title="Delete password"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      {/* Expanded Details Panel */}
                      {expandedPasswords[password.id] && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="bg-white border-t border-gray-200"
                        >
                          <div className="p-6 space-y-4">
                            {/* Account Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Username Section */}
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Username</label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={password.username || ''}
                                    readOnly
                                    className="flex-1 p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                                    autoComplete="off"
                                  />
                                  <button
                                    onClick={() => {
                                      if (password.username) {
                                        copyToClipboard(password.username, `${password.id}-username`);
                                        updateLastUsed(password.id);
                                      }
                                    }}
                                    className="p-2 text-gray-500 hover:text-gray-700 relative"
                                    title="Copy username"
                                    disabled={!password.username}
                                  >
                                    <Copy size={16} />
                                    {copiedId === `${password.id}-username` && (
                                      <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs py-1 px-2 rounded">
                                        Copied!
                                      </span>
                                    )}
                                  </button>
                                </div>
                              </div>

                              {/* Password Section */}
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Password</label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type={showPasswords[password.id] ? "text" : "password"}
                                    value={showPasswords[password.id] ? (decryptedPasswords[password.id] || '') : '••••••••'}
                                    readOnly
                                    className="flex-1 p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                                    autoComplete="off"
                                  />
                                  <button
                                    onClick={async () => {
                                      if (password.encrypted_password) {
                                        let passwordToCopy = decryptedPasswords[password.id];
                                        if (!passwordToCopy) {
                                          passwordToCopy = await decryptPassword(password.encrypted_password, password.id);
                                        }
                                        if (passwordToCopy) {
                                          copyToClipboard(passwordToCopy, `${password.id}-password`);
                                          updateLastUsed(password.id);
                                        }
                                      }
                                    }}
                                    className="p-2 text-gray-500 hover:text-gray-700 relative"
                                    title="Copy password"
                                    disabled={!password.encrypted_password}
                                  >
                                    <Copy size={16} />
                                    {copiedId === `${password.id}-password` && (
                                      <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs py-1 px-2 rounded">
                                        Copied!
                                      </span>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Additional Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* URL */}
                              {password.url && (
                                <div className="space-y-2">
                                  <label className="text-sm font-medium text-gray-700">Website URL</label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="url"
                                      value={password.url}
                                      readOnly
                                      className="flex-1 p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                                    />
                                    <a
                                      href={password.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-2 text-gray-500 hover:text-gray-700"
                                      title="Open website"
                                    >
                                      <ExternalLink size={16} />
                                    </a>
                                  </div>
                                </div>
                              )}

                              {/* Category */}
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Category</label>
                                <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg">
                                  <span className="text-sm text-gray-600">
                                    {defaultCategories.find(cat => cat.id === password.category)?.name || 'Other'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Notes */}
                            {password.notes && (
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Notes</label>
                                <textarea
                                  value={password.notes}
                                  readOnly
                                  className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                                  rows={3}
                                />
                              </div>
                            )}

                            {/* Last Used */}
                            {password.last_used && (
                              <div className="text-sm text-gray-500">
                                Last used: {new Date(password.last_used).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {searchQuery 
                      ? 'No passwords match your search' 
                      : 'No passwords found. Add your first password to get started.'}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Add Password Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Password"
      >
        <div className="space-y-6">
          {/* 1. Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={newPassword.title}
              onChange={(e) => setNewPassword({ ...newPassword, title: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-idecide-accent"
              autoComplete="off"
              placeholder="e.g., Gmail, Facebook, Bank Account"
            />
          </div>

          {/* 2. Website URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Website URL
            </label>
            <input
              type="url"
              value={newPassword.url}
              onChange={(e) => setNewPassword({ ...newPassword, url: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-idecide-accent"
              autoComplete="off"
            />
          </div>

          {/* 3. Category (Dropdown) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={newPassword.category}
              onChange={(e) => setNewPassword({ ...newPassword, category: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-idecide-accent"
              autoComplete="off"
            >
              {defaultCategories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* 4. Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              type="text"
              value={newPassword.username}
              onChange={(e) => setNewPassword({ ...newPassword, username: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-idecide-accent"
              autoComplete="off"
            />
          </div>

          {/* 5. Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              value={newPassword.password}
              onChange={(e) => setNewPassword({ ...newPassword, password: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-idecide-accent"
              autoComplete="new-password"
            />
          </div>

          {/* 6. Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={newPassword.notes}
              onChange={(e) => setNewPassword({ ...newPassword, notes: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-idecide-accent"
              rows={3}
            />
          </div>

          {/* Save or Discard */}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
            >
              Discard
            </Button>
            <Button
              variant="primary"
              onClick={handleSavePassword}
            >
              Save Password
            </Button>
          </div>
        </div>
      </Modal>

      {/* Import Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => {
          setShowImportModal(false);
          setImportType(null);
          setImportResults(null);
          setImportProgress(0);
        }}
        title={`Import from ${importType === 'chrome' ? 'Chrome' : 'CSV'}`}
      >
        <div className="space-y-6">
          {!importing && !importResults && (
            <>
              {importType === 'chrome' ? (
                <div className="space-y-4">
                                     <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                     <Globe className="text-blue-600" size={24} />
                     <div>
                       <h3 className="font-medium text-blue-900">Import from Chrome</h3>
                      <p className="text-sm text-blue-700">
                        Import your saved passwords from Chrome browser. Make sure you have the iDecide Chrome extension installed.
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900">Requirements:</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        iDecide Chrome extension installed
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        Chrome browser with saved passwords
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        Active internet connection
                      </li>
                    </ul>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowImportModal(false)}
                    >
                      Cancel
                    </Button>
                                         <Button
                       variant="primary"
                       onClick={importFromChrome}
                       icon={<Globe size={20} />}
                     >
                       Import from Chrome
                     </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                    <FileText className="text-green-600" size={24} />
                    <div>
                      <h3 className="font-medium text-green-900">Import from CSV</h3>
                      <p className="text-sm text-green-700">
                        Import passwords from a CSV file. The file should have columns for name, username, password, and optionally URL.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900">CSV Format:</h4>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <code className="text-sm text-gray-700">
                        name,username,password,url<br/>
                        Gmail,user@gmail.com,mypassword,https://gmail.com<br/>
                        Facebook,user@facebook.com,fbpassword,https://facebook.com
                      </code>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900">Required Columns:</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <strong>password</strong> - The password (required)
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        <strong>name/title/site</strong> - Site name (optional, will use "Imported Password #")
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        <strong>username/user/email</strong> - Username (optional)
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        <strong>url/website/site</strong> - Website URL (optional)
                      </li>
                    </ul>
                  </div>

                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCSVFileSelect}
                      className="hidden"
                      id="csv-file-input"
                    />
                    <label
                      htmlFor="csv-file-input"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload className="text-gray-400" size={32} />
                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          Click to select CSV file
                        </p>
                        <p className="text-xs text-gray-500">
                          or drag and drop here
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowImportModal(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {importing && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4">
                  <Upload className="text-blue-600 animate-pulse" size={24} />
                </div>
                <h3 className="font-medium text-gray-900 mb-2">
                  Importing passwords...
                </h3>
                <p className="text-sm text-gray-600">
                  Please wait while we import your passwords securely.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progress</span>
                  <span>{importProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${importProgress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          {importResults && (
            <div className="space-y-4">
              <div className="text-center">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-4 ${
                  importResults.failed === 0 ? 'bg-green-100' : 'bg-yellow-100'
                }`}>
                  {importResults.failed === 0 ? (
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                  ) : (
                    <AlertCircle className="text-yellow-600" size={24} />
                  )}
                </div>
                <h3 className="font-medium text-gray-900 mb-2">
                  Import Complete
                </h3>
                <p className="text-sm text-gray-600">
                  {importResults.imported} passwords imported successfully
                  {importResults.failed > 0 && `, ${importResults.failed} failed`}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Summary</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Total processed:</span>
                    <span className="font-medium">{importResults.total}</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Successfully imported:</span>
                    <span className="font-medium">{importResults.imported}</span>
                  </div>
                  {importResults.failed > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Failed:</span>
                      <span className="font-medium">{importResults.failed}</span>
                    </div>
                  )}
                </div>
              </div>

              {importResults.errors.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900">Errors ({importResults.errors.length})</h4>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {importResults.errors.map((error, index) => (
                      <div key={index} className="text-sm text-red-600 bg-red-50 p-2 rounded">
                        {error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowImportModal(false);
                    setImportType(null);
                    setImportResults(null);
                    setImportProgress(0);
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}